import { Card, Textarea, Input, Button } from "@altimateai/lego";

import { CardContent, CardHeader, CardTitle, Tooltip } from "@altimateai/lego";
import { DATAMATE_TEMPLATES, DatamateTemplateType, McpClientInterfaces } from "../constants";
import { Stack } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import useMCPContext from "../useMCPContext";
import { getIntegrationIcon } from "@/pages/teammate/utils";
import useIntegrations from "@/pages/teammate/useIntegrations";
import { DatamatesTracking } from "@/lib/tracking/datamates";

const Role = ({ updateStep }: { updateStep: (step: number) => void }) => {
  const { teammate, updateTeammate } = useMCPContext();
  const { integrationsFromServer } = useIntegrations();

  const handleTemplateSelect = (template: (typeof DATAMATE_TEMPLATES)[number]) => {
    const isCustom = template.type === DatamateTemplateType.CUSTOM;
    if (isCustom) {
      return;
    }

    DatamatesTracking.templateSelected(template.type, template.name);

    const mappedIntegrations = template.integrations
      .map(templateIntegrationId => {
        const serverIntegration = integrationsFromServer?.find(
          integration => integration.id === templateIntegrationId
        );
        return serverIntegration
          ? {
              id: serverIntegration.id,
              tools: serverIntegration.tools || [],
            }
          : null;
      })
      .filter((integration): integration is NonNullable<typeof integration> =>
        Boolean(integration)
      );

    updateTeammate({
      name: template.name,
      description: template.description,
      integrations: mappedIntegrations,
      interfaces: McpClientInterfaces.map(i => i.type),
    });
    updateStep(4);
  };

  return (
    <div>
      {/* Template Selection */}
      <Card className="al-mb-6">
        <CardHeader>
          <CardTitle>1. Start With a Template</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="al-grid al-grid-cols-3 al-gap-6">
            {DATAMATE_TEMPLATES.map(template => (
              <button
                key={template.type}
                onClick={() => handleTemplateSelect(template)}
                className={cn(
                  "al-p-4 al-bg-white al-rounded-xl al-shadow-sm hover:al-shadow-md al-transition-shadow al-duration-200 al-text-left",
                  template.type === DatamateTemplateType.CUSTOM && "!al-bg-bg-brand"
                )}
              >
                <Stack>
                  <div className="al-mb-3">{template.icon}</div>
                  <Stack direction="column" className="al-gap-2">
                    <h3 className="al-font-medium al-text-[#1A1A1A] al-mb-1">{template.name}</h3>
                    <Stack direction="row" className="al-gap-2">
                      {template.integrations.map(integration => (
                        <Tooltip
                          key={integration}
                          title={
                            integrationsFromServer?.find(option => option.id === integration)?.name
                          }
                        >
                          <span>{getIntegrationIcon(integration)}</span>
                        </Tooltip>
                      ))}
                    </Stack>
                    <p className="al-text-sm al-text-[#666666]">{template.description}</p>
                  </Stack>
                </Stack>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="al-mb-6">
        <CardHeader>
          <CardTitle>2. Altimate Setup</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack className="" gap="sm">
            Need help in setting up your Datamate? You can reach out to us over{" "}
            <Button variant="link" asChild className="!al-p-0 !al-h-5 !al-underline">
              <a
                href="https://getdbt.slack.com/archives/C05KPDGRMDW"
                target="_blank"
                rel="noreferrer"
              >
                Slack
              </a>
            </Button>{" "}
            or{" "}
            <Button variant="link" asChild className="!al-p-0 !al-h-5 !al-underline">
              <a href="https://app.myaltimate.com/contactus" target="_blank" rel="noreferrer">
                Chat
              </a>
            </Button>{" "}
            or Book a meeting with us -{" "}
            <Button variant="link" asChild className="!al-p-0 !al-h-5 !al-underline">
              <a
                href="https://calendly.com/andrew-altimate/30min?month=2025-06"
                target="_blank"
                rel="noreferrer"
              >
                Calendly
              </a>
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Create Custom Datamate Form */}
      <Card>
        <CardHeader>
          <CardTitle>3. Create Your Datamate</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack direction="column" className="al-gap-6">
            <div>
              <label
                htmlFor="datamate-name"
                className="al-block al-text-sm al-font-medium al-text-[#1A1A1A] al-mb-2"
              >
                Name
              </label>
              <Input
                id="datamate-name"
                placeholder="Enter your datamate name"
                value={teammate?.name || ""}
                onChange={e => updateTeammate({ name: e.target.value })}
                className="al-bg-white"
              />
              <p className="al-text-xs al-text-[#666666] al-mt-2">
                Choose a descriptive name that reflects the Datamate&apos;s role and purpose
              </p>
            </div>

            <div>
              <label
                htmlFor="datamate-purpose"
                className="al-block al-text-sm al-font-medium al-text-[#1A1A1A] al-mb-2"
              >
                Purpose
              </label>
              <Textarea
                id="datamate-purpose"
                placeholder="I want to create a teammate that specializes in data pipeline creation and optimization. Helps design, implement, and troubleshoot data pipelines across our data ecosystem."
                value={teammate?.description || ""}
                onChange={e => updateTeammate({ description: e.target.value })}
                className="al-min-h-[100px] al-bg-white"
              />
              <p className="al-text-xs al-text-[#666666] al-mt-2">
                Provide a clear description of the Datamate&apos;s purpose and capabilities that you
                want the datamate to possess
              </p>
            </div>
          </Stack>
        </CardContent>
      </Card>
    </div>
  );
};

export default Role;
